import {useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {trpc, type RouterOutput, createMutationErrorHandler} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {Input} from '@common-web/components/ui/input';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@common-web/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@common-web/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {useToast} from '@common-web/hooks/use-toast';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  AlertTriangle,
  Info,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  FileText,
  Layers,
  Activity,
  Search,
} from 'lucide-react';
import {format, formatDistanceToNow} from 'date-fns';

type SyncRun = NonNullable<RouterOutput['internal']['vbaSyncRun']['getById']>;
type GroupSync = RouterOutput['internal']['vbaSyncRun']['getGroupSyncs']['groupSyncs'][number];
type SyncLog = GroupSync['logs'][number];

export function VbaSyncRunDetail() {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const {toast} = useToast();
  const utils = trpc.useUtils();

  // Group sync pagination & filters
  const [gsPage, setGsPage] = useState(1);
  const [gsStatusFilter, setGsStatusFilter] = useState<string>('all');
  const [gsSearch, setGsSearch] = useState('');
  const [gsSearchInput, setGsSearchInput] = useState('');
  const [gsHasErrors, setGsHasErrors] = useState<string>('all');

  // Expand/collapse tracking
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Rerun dialog
  const [showRerunDialog, setShowRerunDialog] = useState(false);

  const {
    data: run,
    isLoading,
    refetch,
    isFetching,
  } = trpc.internal.vbaSyncRun.getById.useQuery(
    {id: id!},
    {
      enabled: !!id,
      refetchInterval: (query) => {
        const data = query.state.data;
        return data?.status === 'RUNNING' ? 3000 : false;
      },
    },
  );

  const {
    data: groupSyncsData,
    isLoading: gsLoading,
    isFetching: gsFetching,
  } = trpc.internal.vbaSyncRun.getGroupSyncs.useQuery(
    {
      syncRunId: id!,
      page: gsPage,
      pageSize: 20,
      status:
        gsStatusFilter === 'all'
          ? undefined
          : (gsStatusFilter as 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED'),
      search: gsSearch || undefined,
      hasErrors: gsHasErrors === 'all' ? undefined : gsHasErrors === 'yes',
    },
    {enabled: !!id},
  );

  const rerunMutation = trpc.internal.vbaSyncRun.rerun.useMutation({
    onSuccess: (data) => {
      toast({
        title: 'Rerun queued',
        description: `${data.count} groups marked for re-sync (${data.syncType})`,
      });
      setShowRerunDialog(false);
      utils.internal.vbaSyncRun.invalidate();
    },
    onError: createMutationErrorHandler('Rerun failed'),
  });

  const toggleGroup = (groupSyncId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupSyncId)) {
        next.delete(groupSyncId);
      } else {
        next.add(groupSyncId);
      }
      return next;
    });
  };

  const expandAll = () => {
    if (!groupSyncsData) return;
    setExpandedGroups(new Set(groupSyncsData.groupSyncs.map((gs) => gs.id)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const handleSearch = () => {
    setGsSearch(gsSearchInput);
    setGsPage(1);
  };

  if (isLoading) {
    return (
      <div className='p-8'>
        <Skeleton className='h-10 w-64 mb-4' />
        <Skeleton className='h-48 w-full mb-4' />
        <Skeleton className='h-96 w-full' />
      </div>
    );
  }

  if (!run) {
    return (
      <div className='p-8'>
        <Card>
          <CardContent className='py-12 text-center'>
            <p className='text-brand-gray'>Sync run not found</p>
            <Button variant='outline' className='mt-4' onClick={() => navigate('/vba-sync')}>
              Back to Sync Runs
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='px-8 py-6 max-w-7xl mx-auto'>
        {/* Back Button */}
        <Button
          variant='ghost'
          className='mb-4 text-brand-gray hover:text-brand-navy'
          onClick={() => navigate('/vba-sync')}
        >
          <ArrowLeft className='h-4 w-4 mr-2' />
          Back to Sync Runs
        </Button>

        {/* Header Card */}
        <Card className='border-gray-200 shadow-sm mb-6'>
          <CardHeader className='bg-gray-50 border-b'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-4'>
                <CardTitle className='text-2xl font-bold text-brand-navy flex items-center gap-3'>
                  <Activity className='h-6 w-6' />
                  Sync Run
                </CardTitle>
                <SyncTypeBadge syncType={run.syncType} />
                <StatusBadge status={run.status} size='lg' />
              </div>
              <div className='flex items-center gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setShowRerunDialog(true)}
                  disabled={run.status === 'RUNNING'}
                >
                  <RotateCcw className='h-4 w-4 mr-2' />
                  Rerun
                </Button>
                <Button variant='outline' size='sm' onClick={() => refetch()} disabled={isFetching}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className='p-6'>
            <div className='grid grid-cols-2 md:grid-cols-5 gap-6'>
              <div>
                <p className='text-sm text-brand-gray mb-1'>Started</p>
                <p className='font-medium text-brand-navy'>
                  {format(new Date(run.startedAt), 'MMM d, yyyy h:mm:ss a')}
                </p>
                <p className='text-xs text-brand-gray'>
                  {formatDistanceToNow(new Date(run.startedAt), {addSuffix: true})}
                </p>
              </div>
              <div>
                <p className='text-sm text-brand-gray mb-1'>Completed</p>
                <p className='font-medium text-brand-navy'>
                  {run.completedAt
                    ? format(new Date(run.completedAt), 'MMM d, yyyy h:mm:ss a')
                    : '-'}
                </p>
              </div>
              <div>
                <p className='text-sm text-brand-gray mb-1'>Duration</p>
                <p className='font-medium font-mono text-brand-navy'>
                  {formatDuration(run.durationMs)}
                </p>
              </div>
              <div>
                <p className='text-sm text-brand-gray mb-1'>Groups</p>
                <div className='flex items-center gap-2'>
                  <span className='font-bold text-brand-navy text-lg'>
                    {run.groupSyncCounts.total}
                  </span>
                  {run.groupSyncCounts.completed > 0 && (
                    <span className='text-emerald text-sm flex items-center gap-0.5'>
                      <CheckCircle2 className='h-3 w-3' />
                      {run.groupSyncCounts.completed}
                    </span>
                  )}
                  {run.groupSyncCounts.failed > 0 && (
                    <span className='text-red-500 text-sm flex items-center gap-0.5'>
                      <XCircle className='h-3 w-3' />
                      {run.groupSyncCounts.failed}
                    </span>
                  )}
                  {run.groupSyncCounts.running > 0 && (
                    <span className='text-brand-blue text-sm flex items-center gap-0.5'>
                      <Loader2 className='h-3 w-3 animate-spin' />
                      {run.groupSyncCounts.running}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <p className='text-sm text-brand-gray mb-1'>Issues</p>
                <div className='flex items-center gap-3'>
                  {run.totalErrors > 0 && (
                    <span className='text-red-500 text-sm font-medium flex items-center gap-1'>
                      <XCircle className='h-3 w-3' />
                      {run.totalErrors} errors
                    </span>
                  )}
                  {run.totalWarnings > 0 && (
                    <span className='text-amber text-sm font-medium flex items-center gap-1'>
                      <AlertTriangle className='h-3 w-3' />
                      {run.totalWarnings} warnings
                    </span>
                  )}
                  {run.totalErrors === 0 && run.totalWarnings === 0 && (
                    <span className='text-emerald text-sm font-medium flex items-center gap-1'>
                      <CheckCircle2 className='h-3 w-3' />
                      Clean
                    </span>
                  )}
                </div>
              </div>
            </div>

            {run.filterCriteria && (
              <div className='mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg'>
                <p className='text-xs font-medium text-brand-gray mb-1'>Filter Criteria</p>
                <pre className='text-xs font-mono text-brand-navy'>
                  {JSON.stringify(run.filterCriteria, null, 2)}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Group Syncs */}
        <Card className='border-gray-200 shadow-sm'>
          <CardHeader className='bg-gray-50 border-b'>
            <div className='flex items-center justify-between'>
              <CardTitle className='text-lg font-semibold text-brand-navy flex items-center gap-2'>
                <Layers className='h-5 w-5' />
                Group Syncs
                {groupSyncsData && (
                  <Badge className='bg-brand-blue/10 text-brand-blue border-0 font-medium ml-2'>
                    {groupSyncsData.totalCount} total
                  </Badge>
                )}
              </CardTitle>
              <div className='flex items-center gap-2'>
                <Button variant='ghost' size='sm' onClick={expandAll}>
                  Expand All
                </Button>
                <Button variant='ghost' size='sm' onClick={collapseAll}>
                  Collapse All
                </Button>
              </div>
            </div>
          </CardHeader>

          {/* Filters */}
          <div className='px-4 py-3 border-b bg-white flex flex-wrap items-center gap-3'>
            <div className='flex items-center gap-2'>
              <Input
                placeholder='Search group name or VBA ID...'
                className='w-64'
                value={gsSearchInput}
                onChange={(e) => setGsSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
              />
              <Button variant='outline' size='sm' onClick={handleSearch}>
                <Search className='h-4 w-4' />
              </Button>
            </div>

            <Select
              value={gsStatusFilter}
              onValueChange={(v) => {
                setGsStatusFilter(v);
                setGsPage(1);
              }}
            >
              <SelectTrigger className='w-40'>
                <SelectValue placeholder='All Status' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Status</SelectItem>
                <SelectItem value='COMPLETED'>Completed</SelectItem>
                <SelectItem value='FAILED'>Failed</SelectItem>
                <SelectItem value='RUNNING'>Running</SelectItem>
                <SelectItem value='PENDING'>Pending</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={gsHasErrors}
              onValueChange={(v) => {
                setGsHasErrors(v);
                setGsPage(1);
              }}
            >
              <SelectTrigger className='w-40'>
                <SelectValue placeholder='Errors' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Groups</SelectItem>
                <SelectItem value='yes'>With Errors</SelectItem>
                <SelectItem value='no'>No Errors</SelectItem>
              </SelectContent>
            </Select>

            {gsFetching && !gsLoading && (
              <Loader2 className='h-4 w-4 animate-spin text-brand-gray' />
            )}
          </div>

          <CardContent className='p-4'>
            {gsLoading ? (
              <div className='space-y-2'>
                {Array.from({length: 5}).map((_, i) => (
                  <Skeleton key={i} className='h-14 w-full' />
                ))}
              </div>
            ) : (
              <div className='space-y-2'>
                {groupSyncsData?.groupSyncs.map((gs) => (
                  <GroupSyncRow
                    key={gs.id}
                    groupSync={gs}
                    isExpanded={expandedGroups.has(gs.id)}
                    onToggle={() => toggleGroup(gs.id)}
                  />
                ))}

                {(!groupSyncsData || groupSyncsData.groupSyncs.length === 0) && (
                  <div className='text-center py-12 text-brand-gray'>
                    No group syncs found matching filters
                  </div>
                )}
              </div>
            )}

            {/* Pagination */}
            {groupSyncsData && groupSyncsData.totalPages > 1 && (
              <div className='flex items-center justify-between mt-4 pt-4 border-t'>
                <span className='text-sm text-brand-gray'>
                  Page {gsPage} of {groupSyncsData.totalPages} ({groupSyncsData.totalCount} total)
                </span>
                <div className='flex gap-2'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setGsPage((p) => Math.max(1, p - 1))}
                    disabled={gsPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setGsPage((p) => p + 1)}
                    disabled={gsPage >= groupSyncsData.totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Rerun Dialog */}
      <Dialog open={showRerunDialog} onOpenChange={setShowRerunDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rerun Sync</DialogTitle>
            <DialogDescription>
              Mark groups from this run as needing re-sync. The sync will execute on the next
              scheduled job run.
              {run.groupSyncCounts.failed > 0 && (
                <span className='block mt-2 text-red-600'>
                  {run.groupSyncCounts.failed} group(s) failed in this run.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className='flex gap-2 sm:gap-2'>
            <Button variant='outline' onClick={() => setShowRerunDialog(false)}>
              Cancel
            </Button>
            {run.groupSyncCounts.failed > 0 && (
              <Button
                variant='outline'
                className='border-red-200 text-red-700 hover:bg-red-50'
                onClick={() => rerunMutation.mutate({syncRunId: run.id, scope: 'failed_only'})}
                disabled={rerunMutation.isPending}
              >
                {rerunMutation.isPending ? (
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                ) : (
                  <RotateCcw className='h-4 w-4 mr-2' />
                )}
                Rerun Failed Only ({run.groupSyncCounts.failed})
              </Button>
            )}
            <Button
              onClick={() => rerunMutation.mutate({syncRunId: run.id, scope: 'all'})}
              disabled={rerunMutation.isPending}
            >
              {rerunMutation.isPending ? (
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
              ) : (
                <RotateCcw className='h-4 w-4 mr-2' />
              )}
              Rerun All ({run.groupSyncCounts.total})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────

function GroupSyncRow({
  groupSync,
  isExpanded,
  onToggle,
}: {
  groupSync: GroupSync;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const [showLogs, setShowLogs] = useState(true);
  const [showLogSummary, setShowLogSummary] = useState(false);

  const borderColor =
    groupSync.status === 'FAILED'
      ? 'border-red-200'
      : groupSync.status === 'RUNNING'
        ? 'border-brand-blue/30'
        : 'border-gray-200';

  const bgColor = groupSync.status === 'FAILED' ? 'bg-red-50/30' : '';

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div className={`border rounded-lg overflow-hidden ${borderColor} ${bgColor}`}>
        <CollapsibleTrigger asChild>
          <button className='w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50/50 transition-colors text-left'>
            <div className='flex items-center gap-4 min-w-0'>
              {isExpanded ? (
                <ChevronDown className='h-4 w-4 text-brand-gray shrink-0' />
              ) : (
                <ChevronRight className='h-4 w-4 text-brand-gray shrink-0' />
              )}
              <div className='min-w-0'>
                <div className='flex items-center gap-2'>
                  <span className='font-medium text-brand-navy truncate'>
                    {groupSync.group.name || 'Unknown Group'}
                  </span>
                  {groupSync.group.vbaGroupId && (
                    <span className='text-xs text-brand-gray font-mono'>
                      VBA: {groupSync.group.vbaGroupId}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className='flex items-center gap-4 shrink-0'>
              {groupSync.errorCount > 0 && (
                <span className='text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded-full font-medium'>
                  {groupSync.errorCount} errors
                </span>
              )}
              {groupSync.warnCount > 0 && (
                <span className='text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full font-medium'>
                  {groupSync.warnCount} warnings
                </span>
              )}
              <span className='text-xs font-mono text-brand-gray'>
                {formatDuration(groupSync.durationMs)}
              </span>
              <StatusBadge status={groupSync.status} />
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className='border-t border-gray-100'>
            {/* Stats Grid */}
            <div className='px-4 py-3 bg-gray-50/50'>
              <div className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3'>
                <StatPill label='Subs Created' value={groupSync.subscribersCreated} />
                <StatPill label='Subs Existed' value={groupSync.subscribersExisted} />
                <StatPill label='Members Created' value={groupSync.membersCreated} />
                <StatPill label='Members Existed' value={groupSync.membersExisted} />
                <StatPill label='Enrollments Created' value={groupSync.enrollmentsCreated} />
                <StatPill label='Enrollments Existed' value={groupSync.enrollmentsExisted} />
                <StatPill label='Sub Plans Created' value={groupSync.subPlansCreated} />
                <StatPill label='Member Plans Created' value={groupSync.memberPlansCreated} />
                <StatPill label='Sub Plans Terminated' value={groupSync.subPlansTerminated} />
                <StatPill label='Mem Plans Terminated' value={groupSync.memPlansTerminated} />
                <StatPill label='Errors' value={groupSync.errorsCount} highlight='red' />
                <StatPill label='Warnings' value={groupSync.warningsCount} highlight='amber' />
              </div>
            </div>

            {/* Timing */}
            <div className='px-4 py-2 border-t border-gray-100 flex items-center gap-6 text-xs text-brand-gray'>
              <span>Started: {format(new Date(groupSync.startedAt), 'h:mm:ss.SSS a')}</span>
              {groupSync.completedAt && (
                <span>Completed: {format(new Date(groupSync.completedAt), 'h:mm:ss.SSS a')}</span>
              )}
              <span>Duration: {formatDuration(groupSync.durationMs)}</span>
            </div>

            {/* Error Message */}
            {groupSync.errorMessage && (
              <div className='mx-4 my-2 p-3 bg-red-50 border border-red-200 rounded-lg'>
                <div className='flex items-start gap-2'>
                  <XCircle className='h-4 w-4 text-red-500 mt-0.5 shrink-0' />
                  <div className='min-w-0'>
                    <p className='font-medium text-red-800 text-sm'>Error Message</p>
                    <p className='text-red-700 font-mono text-xs mt-1 whitespace-pre-wrap break-all'>
                      {groupSync.errorMessage}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Structured Logs */}
            {groupSync.logs.length > 0 && (
              <div className='px-4 py-3 border-t border-gray-100'>
                <div className='flex items-center justify-between mb-2'>
                  <button
                    className='flex items-center gap-2 text-sm font-medium text-brand-navy hover:text-brand-blue transition-colors'
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLogs(!showLogs);
                    }}
                  >
                    <FileText className='h-4 w-4' />
                    Structured Logs ({groupSync.logs.length})
                    {showLogs ? (
                      <ChevronDown className='h-3 w-3' />
                    ) : (
                      <ChevronRight className='h-3 w-3' />
                    )}
                  </button>
                  <div className='flex items-center gap-1'>
                    {groupSync.errorCount > 0 && (
                      <Badge className='bg-red-100 text-red-700 border-0 text-xs'>
                        {groupSync.errorCount} E
                      </Badge>
                    )}
                    {groupSync.warnCount > 0 && (
                      <Badge className='bg-amber-100 text-amber-700 border-0 text-xs'>
                        {groupSync.warnCount} W
                      </Badge>
                    )}
                    {groupSync.infoCount > 0 && (
                      <Badge className='bg-gray-100 text-gray-600 border-0 text-xs'>
                        {groupSync.infoCount} I
                      </Badge>
                    )}
                  </div>
                </div>
                {showLogs && (
                  <div className='space-y-1 max-h-96 overflow-y-auto'>
                    {groupSync.logs.map((log) => (
                      <LogEntry key={log.id} log={log} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Raw Log Summary */}
            {groupSync.logSummary && (
              <div className='px-4 py-3 border-t border-gray-100'>
                <button
                  className='flex items-center gap-2 text-sm font-medium text-brand-navy hover:text-brand-blue transition-colors mb-2'
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLogSummary(!showLogSummary);
                  }}
                >
                  <Activity className='h-4 w-4' />
                  Raw Log Output
                  {showLogSummary ? (
                    <ChevronDown className='h-3 w-3' />
                  ) : (
                    <ChevronRight className='h-3 w-3' />
                  )}
                </button>
                {showLogSummary && (
                  <pre className='bg-gray-900 text-gray-100 p-3 rounded-lg overflow-auto text-xs font-mono max-h-64 whitespace-pre-wrap'>
                    {groupSync.logSummary}
                  </pre>
                )}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function LogEntry({log}: {log: SyncLog}) {
  const [showData, setShowData] = useState(false);

  const levelClass =
    log.level === 'ERROR'
      ? 'bg-red-50 border-red-200 text-red-800'
      : log.level === 'WARN'
        ? 'bg-amber-50 border-amber-200 text-amber-800'
        : 'bg-gray-50 border-gray-200 text-gray-700';

  const levelIcon =
    log.level === 'ERROR' ? (
      <XCircle className='h-3.5 w-3.5 text-red-500 shrink-0' />
    ) : log.level === 'WARN' ? (
      <AlertTriangle className='h-3.5 w-3.5 text-amber shrink-0' />
    ) : (
      <Info className='h-3.5 w-3.5 text-brand-gray shrink-0' />
    );

  return (
    <div className={`px-3 py-2 rounded-sm border text-xs ${levelClass}`}>
      <div className='flex items-start gap-2'>
        {levelIcon}
        <div className='flex-1 min-w-0'>
          <div className='flex items-center justify-between gap-2'>
            <div className='flex items-center gap-2'>
              <span className='font-semibold'>{log.level}</span>
              {log.context && (
                <Badge className='bg-white/50 text-current border-current/20 text-[10px] px-1.5 py-0'>
                  {log.context}
                </Badge>
              )}
              {log.entityId && (
                <span className='font-mono text-[10px] opacity-70'>[{log.entityId}]</span>
              )}
            </div>
            <span className='text-[10px] opacity-60 shrink-0'>
              {format(new Date(log.timestamp), 'h:mm:ss.SSS a')}
            </span>
          </div>
          <p className='mt-0.5 font-mono whitespace-pre-wrap break-all'>{log.message}</p>
          {log.data && (
            <>
              <button
                className='mt-1 text-[10px] underline opacity-70 hover:opacity-100'
                onClick={(e) => {
                  e.stopPropagation();
                  setShowData(!showData);
                }}
              >
                {showData ? 'Hide data' : 'Show data'}
              </button>
              {showData && (
                <pre className='mt-1 bg-white/50 p-2 rounded-sm overflow-auto text-[10px]'>
                  {JSON.stringify(log.data, null, 2)}
                </pre>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatPill({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number | null | undefined;
  highlight?: 'red' | 'amber';
}) {
  if (value === null || value === undefined) return null;

  const colorClass =
    highlight === 'red' && value > 0
      ? 'text-red-700 bg-red-50 border-red-200'
      : highlight === 'amber' && value > 0
        ? 'text-amber-700 bg-amber-50 border-amber-200'
        : 'text-brand-navy bg-white border-gray-200';

  return (
    <div className={`border rounded-md px-2.5 py-1.5 ${colorClass}`}>
      <div className='text-[10px] font-medium uppercase tracking-wider opacity-70'>{label}</div>
      <div className='text-sm font-bold'>{value}</div>
    </div>
  );
}

// ─── Shared badge helpers ──────────────────────────────────────────

function StatusBadge({status, size = 'sm'}: {status: string; size?: 'sm' | 'lg'}) {
  const sizeClasses = size === 'lg' ? 'text-base px-4 py-2' : '';
  const iconSize = size === 'lg' ? 'h-4 w-4 mr-2' : 'h-3 w-3 mr-1';

  switch (status) {
    case 'COMPLETED':
      return (
        <Badge className={`bg-emerald text-white border-0 font-medium ${sizeClasses}`}>
          <CheckCircle2 className={iconSize} />
          Completed
        </Badge>
      );
    case 'FAILED':
      return (
        <Badge className={`bg-red-500 text-white border-0 font-medium ${sizeClasses}`}>
          <XCircle className={iconSize} />
          Failed
        </Badge>
      );
    case 'RUNNING':
      return (
        <Badge className={`bg-brand-blue text-white border-0 font-medium ${sizeClasses}`}>
          <Loader2 className={`${iconSize} animate-spin`} />
          Running
        </Badge>
      );
    case 'PENDING':
      return (
        <Badge className={`bg-brand-gray text-white border-0 font-medium ${sizeClasses}`}>
          <Clock className={iconSize} />
          Pending
        </Badge>
      );
    default:
      return <Badge>{status}</Badge>;
  }
}

function SyncTypeBadge({syncType}: {syncType: string}) {
  const colors: Record<string, string> = {
    MEMBER: 'bg-purple-100 text-purple-700 border-purple-200',
    GROUP: 'bg-blue-100 text-blue-700 border-blue-200',
    FULL_SYNC: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    PLAN_SYNC: 'bg-teal-100 text-teal-700 border-teal-200',
    ENROLLMENT_SYNC: 'bg-orange-100 text-orange-700 border-orange-200',
  };
  return (
    <Badge className={`${colors[syncType] || 'bg-gray-100 text-gray-700'} font-medium`}>
      {syncType.replace(/_/g, ' ')}
    </Badge>
  );
}

function formatDuration(ms: number | null | undefined) {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
  if (ms < 3600000) return `${(ms / 60000).toFixed(2)}m`;
  return `${(ms / 3600000).toFixed(2)}h`;
}
